/**
 * AI System Background Component
 * Provides the signature gradient background for AI agent interfaces
 * This creates the distinctive visual environment for AI interactions
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface AISystemBackgroundProps {
  className?: string;
  variant?: 'default' | 'subtle' | 'intense';
}

export const AISystemBackground: React.FC<AISystemBackgroundProps> = ({ 
  className,
  variant = 'default' 
}) => {
  // Adjust opacity and intensity based on variant
  const getVariantClasses = () => {
    switch (variant) {
      case 'subtle':
        return {
          primary: 'from-purple-500/3 via-accent/5 to-transparent dark:from-purple-900/15 dark:via-accent/10 dark:to-slate-900/10',
          radial: 'from-purple-500/5 via-accent/3 to-transparent dark:from-purple-800/15 dark:via-accent/8 dark:to-slate-900/15',
          orb1: 'from-accent/5 to-purple-500/5 dark:from-accent/15 dark:to-purple-600/10',
          orb2: 'from-purple-500/5 to-accent/5 dark:from-purple-600/10 dark:to-accent/15',
          overlay: 'bg-white/20 dark:bg-transparent'
        };
      case 'intense':
        return {
          primary: 'from-purple-500/10 via-accent/15 to-transparent dark:from-purple-900/35 dark:via-accent/30 dark:to-slate-900/30',
          radial: 'from-purple-500/15 via-accent/8 to-transparent dark:from-purple-800/40 dark:via-accent/20 dark:to-slate-900/40',
          orb1: 'from-accent/15 to-purple-500/15 dark:from-accent/40 dark:to-purple-600/30',
          orb2: 'from-purple-500/15 to-accent/15 dark:from-purple-600/30 dark:to-accent/40',
          overlay: 'bg-white/60 dark:bg-transparent'
        };
      default:
        return {
          primary: 'from-purple-500/5 via-accent/10 to-transparent dark:from-purple-900/25 dark:via-accent/20 dark:to-slate-900/20',
          radial: 'from-purple-500/10 via-accent/5 to-transparent dark:from-purple-800/30 dark:via-accent/15 dark:to-slate-900/30',
          orb1: 'from-accent/10 to-purple-500/10 dark:from-accent/30 dark:to-purple-600/20',
          orb2: 'from-purple-500/10 to-accent/10 dark:from-purple-600/20 dark:to-accent/30',
          overlay: 'bg-white/40 dark:bg-transparent'
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <div className={cn("absolute inset-0 -z-10 overflow-hidden", className)}>
      {/* Primary gradient layer */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br",
        variantClasses.primary
      )} />
      
      {/* Radial gradient overlay */}
      <div className={cn(
        "absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))]",
        variantClasses.radial
      )} />
      
      {/* Backdrop blur effect */}
      <div className="absolute inset-0 backdrop-blur-[100px] mix-blend-normal opacity-40" />
      
      {/* Animated gradient orbs */}
      <div className={cn(
        "absolute top-[25%] right-[33%] w-[40%] h-[40%] rounded-full bg-gradient-to-r blur-3xl animate-pulse",
        variantClasses.orb1
      )} />
      
      <div className={cn(
        "absolute bottom-[33%] left-[25%] w-[30%] h-[30%] rounded-full bg-gradient-to-r blur-3xl animate-pulse delay-1000",
        variantClasses.orb2
      )} />
      
      {/* Light mode overlay */}
      <div className={cn("absolute inset-0", variantClasses.overlay)} />
      
      {/* Animated shimmer effect */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,transparent_0%,rgba(255,255,255,0.05)_15%,transparent_30%)] animate-[gradient_8s_ease_infinite]" />
    </div>
  );
};

export default AISystemBackground; 